// src/features/product-tours/ProductTourProvider.tsx
// API unique : startTour('help_guides_v1') — auto-prompt OU clic FAQ / guides.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useOptionalLayoutShell } from '../../contexts/LayoutShellContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { isTourDefinitionEligible } from './guideEligibility';
import { getProductTour } from './tourRegistry';
import { writeLocalSeenTour } from './seenToursStorage';
import { scrollTourTargetIntoView } from './scrollTourTargetIntoView';
import { isNavTourSelector, tourFirstStepNeedsMenu } from './mobileTourNav';
import { syncTourNextDoneButton } from './tourStepHelpers';
import type { ProductTourId } from './types';

export type StartTourOptions = {
  /** Marquer le tour comme vu à la fin (défaut true). */
  markSeen?: boolean;
  /** Index de départ (défaut 0). */
  stepIndex?: number;
};

type ProductTourContextValue = {
  startTour: (tourId: ProductTourId, options?: StartTourOptions) => void;
  stopTour: () => void;
  isTourRunning: () => boolean;
};

const ProductTourContext = createContext<ProductTourContextValue | undefined>(undefined);

export const ProductTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useAppTranslation('common');
  const { user, userProfile, currentBusiness, patchUserProfile } = useAuth();
  const permissions = usePermissions();
  const isMobileViewport = useMediaQuery('(max-width: 767px)');
  const layoutShell = useOptionalLayoutShell();
  const navigate = useNavigate();
  const location = useLocation();
  const driverRef = useRef<Driver | null>(null);
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const layoutShellRef = useRef(layoutShell);
  layoutShellRef.current = layoutShell;

  const availabilityCtx = useMemo(
    () => ({
      business: currentBusiness,
      permissions,
      isMobileViewport,
    }),
    [currentBusiness, permissions, isMobileViewport]
  );

  const markSeen = useCallback(
    async (tourId: ProductTourId) => {
      const uid = user?.uid;
      if (!uid) return;
      writeLocalSeenTour(uid, tourId);
      const nextSeen = { ...(userProfile?.seenTours || {}), [tourId]: true };
      patchUserProfile({ seenTours: nextSeen });
      try {
        await updateDoc(doc(db, COLLECTIONS.users, uid), {
          [`seenTours.${tourId}`]: true,
        });
      } catch (error) {
        console.warn('Impossible d’enregistrer seenTours:', error);
      }
    },
    [user?.uid, userProfile?.seenTours, patchUserProfile]
  );

  const stopTour = useCallback(() => {
    try {
      driverRef.current?.destroy();
    } catch {
      // ignore
    }
    document.body.classList.remove('nauvatek-tour-active');
    driverRef.current = null;
  }, []);

  const startTour = useCallback(
    (tourId: ProductTourId, options?: StartTourOptions) => {
      const def = getProductTour(tourId);
      if (!def) {
        console.warn(`[product-tours] Tour inconnu: ${tourId}`);
        return;
      }

      if (!isTourDefinitionEligible(def, availabilityCtx)) {
        message.info(t('tours.common.unavailable'));
        return;
      }

      stopTour();

      const shouldMarkSeen = options?.markSeen !== false;
      const pathname = pathnameRef.current;
      const shell = layoutShellRef.current;
      const nextBtnText = t('tours.common.next');
      const doneBtnText = t('tours.common.done');
      const scrollBlock = def.highlightScrollBlock ?? 'center';

      const steps = def.buildSteps({
        t: (key, opts) => String(t(key, opts)),
        navigate: (to) => navigate(to),
        pathname,
        layoutShell: shell,
        business: currentBusiness,
        permissions,
      });

      if (steps.length === 0) {
        message.info(t('tours.common.unavailable'));
        return;
      }

      const runDriver = () => {
        const d = driver({
          showProgress: true,
          animate: true,
          allowClose: true,
          allowScroll: true,
          smoothScroll: true,
          overlayOpacity: 0.55,
          stagePadding: 8,
          stageRadius: 10,
          // true : éléments absents (panier vide, permission, feature) → skip sans planter
          // waitForElement : navigation + ouverture drawer (ne pas trop long pour les skips)
          skipMissingElement: true,
          waitForElement: 2500,
          popoverClass: 'nauvatek-tour-popover',
          nextBtnText,
          prevBtnText: t('tours.common.prev'),
          doneBtnText,
          progressText: t('tours.common.progress'),
          onPopoverRender: (popover, { state }) => {
            syncTourNextDoneButton({
              wrapper: popover.wrapper,
              activeIndex: state.activeIndex ?? 0,
              stepsLength: steps.length,
              nextBtnText,
              doneBtnText,
            });
          },
          onHighlightStarted: (element, step) => {
            document.body.classList.add('nauvatek-tour-active');
            if (isNavTourSelector(step?.element)) {
              layoutShellRef.current.openMenu();
            }
            const block =
              typeof step?.element === 'string' && step.element.includes('help-tabs')
                ? 'start'
                : scrollBlock;
            scrollTourTargetIntoView(element, { block });
          },
          onHighlighted: (element, step, { driver: drv }) => {
            const block =
              typeof step?.element === 'string' && step.element.includes('help-tabs')
                ? 'start'
                : scrollBlock;
            scrollTourTargetIntoView(element, { block });
            window.setTimeout(() => {
              try {
                drv.refresh();
              } catch {
                // ignore
              }
            }, 350);
          },
          onDestroyed: () => {
            document.body.classList.remove('nauvatek-tour-active');
            if (shouldMarkSeen) {
              void markSeen(tourId);
            }
            driverRef.current = null;
          },
          steps,
        });

        driverRef.current = d;
        d.drive(options?.stepIndex ?? 0);
      };

      if (tourFirstStepNeedsMenu(steps)) {
        const alreadyOpen = shell.isMenuOpen;
        shell.openMenu();
        window.setTimeout(runDriver, alreadyOpen ? 50 : 450);
        return;
      }

      runDriver();
    },
    [t, navigate, stopTour, markSeen, availabilityCtx, currentBusiness, permissions]
  );

  useEffect(() => () => stopTour(), [stopTour]);

  const value = useMemo(
    () => ({
      startTour,
      stopTour,
      isTourRunning: () => driverRef.current?.isActive() === true,
    }),
    [startTour, stopTour]
  );

  return (
    <ProductTourContext.Provider value={value}>{children}</ProductTourContext.Provider>
  );
};

export function useProductTour(): ProductTourContextValue {
  const ctx = useContext(ProductTourContext);
  if (!ctx) {
    throw new Error('useProductTour must be used within ProductTourProvider');
  }
  return ctx;
}
